import CheckCircleBroken from "@/assets/icons/check-circle-broken.svg";
import CircleIcon from "@/assets/icons/circle.svg";
import ForwardIcon from "@/assets/icons/forward.svg";
import MailClosedIcon from "@/assets/icons/mail-closed.svg";
import MailIcon from "@/assets/icons/mail.svg";
import ReplyToAllIcon from "@/assets/icons/reply-all.svg";
import ReplyIcon from "@/assets/icons/reply.svg";
import TargetBarIcon from "@/assets/icons/target-bar.svg";
import TargetIcon from "@/assets/icons/target.svg";
import TrashIcon from "@/assets/icons/trash.svg";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import MailActionType from "@/domain/enums/MailActionType";
import SmartLabel from "@/domain/enums/SmartLabel";
import useMailActions from "@/hooks/useMailActions";
import { isSameCopy } from "@/lib/mailIdentity";
import { cn } from "@/lib/utils";
import mailStore, {
    setMailActionType,
    setOpenedMail,
} from "@/stores/mailStore";
import { shallow, useSelector } from "@tanstack/react-store";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StopPropagateDiv from "../StopPropagateDiv";
import { Button } from "../ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";

export enum MailActionVariant {
    MAIL_LIST = "mailList",
    SINGLE_MAIL = "singleMail",
}

export enum FocusTarget {
    EDITOR = "editor",
    TO_INPUT = "toInput",
}

type MailActionButtonsProps = {
    actions: MailActionItem[];
    variant?: MailActionVariant;
    onTooltipOpenChange?: (open: boolean) => void;
};

const MailActionButtons = ({
    actions,
    variant = MailActionVariant.MAIL_LIST,
    onTooltipOpenChange,
}: MailActionButtonsProps) => {
    const size =
        variant === MailActionVariant.MAIL_LIST ? "tinyCircle" : "smallCircle";
    const buttonVariant =
        variant === MailActionVariant.MAIL_LIST ? "invisible" : "action";

    const renderIconButton = (iconPath: string, onClick: () => void) => (
        <Button
            className="hover:bg-surface-activeFgHover"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            size={size}
            variant={buttonVariant}
        >
            <img alt="Icon" className="h-4" src={iconPath} />
        </Button>
    );

    return (
        <>
            {actions.map((action) => (
                <TooltipProvider key={action.icon} delayDuration={100}>
                    <Tooltip onOpenChange={onTooltipOpenChange}>
                        <TooltipTrigger asChild>
                            {renderIconButton(action.icon, action.action)}
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{action.tooltip}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ))}
        </>
    );
};

type MailActionItem = {
    icon: string;
    action: () => void;
    tooltip: string;
};

type MailActionProps = {
    className?: string;
    mail: Email;
    variant?: MailActionVariant;
    handleProcessingFocusMail?: () => void;
    listId: MailsListId;
    setMailInputExpanded: (
        mailInputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    setSelectedMails?: Dispatch<SetStateAction<Email[]>>;
    onTooltipOpenChange?: (open: boolean) => void;
};

const MailAction = ({
    className,
    mail,
    variant = MailActionVariant.MAIL_LIST,
    handleProcessingFocusMail,
    listId,
    setMailInputExpanded,
    setSelectedMails,
    onTooltipOpenChange,
}: MailActionProps) => {
    const { t } = useTranslation();
    const { setReadStatus, setProcessStatus, setMailFocus, deleteMail } =
        useMailActions();
    const { isVistassistantResult, onMailClick } = useVistassistantMail();
    const openedMail = useSelector(mailStore, (state) => state.openedMail, {
        compare: shallow,
    });
    const mailActionType = useSelector(
        mailStore,
        (state) => state.mailActionType,
    );
    const isOpened = !!openedMail && isSameCopy(openedMail, mail);

    const singleActionToastKey = (key: string) =>
        variant === MailActionVariant.SINGLE_MAIL ? key : undefined;

    // Assistant has no inline MailThread (see EmailView): its reply/forward editor
    // is in VistassistantMailFullView, reached via onMailClick. Main list unchanged.
    const handleReply = () => {
        if (isVistassistantResult) {
            onMailClick?.(mail, listId, MailActionType.REPLY);

            return;
        }
        if (!isOpened) {
            setOpenedMail(mail, { actionType: MailActionType.REPLY });
        } else if (mailActionType !== MailActionType.REPLY) {
            setMailActionType(MailActionType.REPLY);
        }
        setMailInputExpanded(true, FocusTarget.EDITOR);
    };

    const handleReplyAll = () => {
        if (isVistassistantResult) {
            onMailClick?.(mail, listId, MailActionType.REPLY_ALL);

            return;
        }
        if (!isOpened) {
            setOpenedMail(mail, { actionType: MailActionType.REPLY_ALL });
        } else if (mailActionType !== MailActionType.REPLY_ALL) {
            setMailActionType(MailActionType.REPLY_ALL);
        }
        setMailInputExpanded(true, FocusTarget.EDITOR);
    };

    const handleForward = () => {
        if (isVistassistantResult) {
            onMailClick?.(mail, listId, MailActionType.FORWARD);

            return;
        }
        if (!isOpened) {
            setOpenedMail(mail, { actionType: MailActionType.FORWARD });
        } else if (mailActionType !== MailActionType.FORWARD) {
            setMailActionType(MailActionType.FORWARD);
        }
        setMailInputExpanded(true, FocusTarget.TO_INPUT);
    };

    const handleActionWithSelectedMails = (action: () => void) => {
        action();
        if (setSelectedMails) {
            setSelectedMails((previousSelectedMails: Email[]) =>
                previousSelectedMails.filter(
                    (m) => m.internetMessageId !== mail.internetMessageId,
                ),
            );
        }
    };

    const mailIsAnalyzed = mail.aiDetails !== undefined;

    const actions = [
        {
            icon: mail.isRead ? MailClosedIcon : MailIcon,
            action: () => {
                handleActionWithSelectedMails(() =>
                    setReadStatus.mutateAsync({
                        listId,
                        mail,
                        readStatus: !mail.isRead,
                        successToastKey: singleActionToastKey(
                            mail.isRead
                                ? "success.singleActions.markedAsUnread"
                                : "success.singleActions.markedAsRead",
                        ),
                    }),
                );
            },
            tooltip: mail.isRead
                ? t("emails.actions.markAsUnread")
                : t("emails.actions.markAsRead"),
        },
        ...(mailIsAnalyzed
            ? [
                  {
                      icon: mail.isUserProcessed
                          ? CircleIcon
                          : CheckCircleBroken,
                      action: () => {
                          handleActionWithSelectedMails(() => {
                              if (handleProcessingFocusMail) {
                                  handleProcessingFocusMail();
                              } else {
                                  setProcessStatus.mutateAsync({
                                      listId,
                                      mail,
                                      isUserProcessed: !mail.isUserProcessed,
                                      successToastKey: singleActionToastKey(
                                          mail.isUserProcessed
                                              ? "success.singleActions.markedAsUnprocessed"
                                              : "success.singleActions.markedAsProcessed",
                                      ),
                                  });
                              }
                          });
                      },
                      tooltip: mail.isUserProcessed
                          ? t("emails.actions.markAsUnprocessed")
                          : t("emails.actions.markAsProcessed"),
                  },
                  ...(listId !== SmartLabel.Traites
                      ? [
                            {
                                icon: mail.isFocus ? TargetBarIcon : TargetIcon,
                                action: () => {
                                    handleActionWithSelectedMails(() =>
                                        setMailFocus.mutateAsync({
                                            listId,
                                            mail,
                                            isFocus: !mail.isFocus,
                                            successToastKey:
                                                singleActionToastKey(
                                                    mail.isFocus
                                                        ? "success.singleActions.markedAsUnfocus"
                                                        : "success.singleActions.markedAsFocus",
                                                ),
                                        }),
                                    );
                                },
                                tooltip: mail.isFocus
                                    ? t("emails.actions.removeFromFocus")
                                    : t("emails.actions.moveToFocus"),
                            },
                        ]
                      : []),
              ]
            : []),
        {
            icon: TrashIcon,
            action: async () => {
                handleActionWithSelectedMails(() =>
                    deleteMail.mutateAsync({
                        listId,
                        mail,
                        successToastKey: singleActionToastKey(
                            "success.singleActions.deleted",
                        ),
                    }),
                );
            },
            tooltip: t("emails.actions.delete"),
        },
        {
            icon: ReplyIcon,
            action: handleReply,
            tooltip: t("emails.actions.reply"),
        },
        {
            icon: ReplyToAllIcon,
            action: handleReplyAll,
            tooltip: t("emails.actions.replyAll"),
        },
        {
            icon: ForwardIcon,
            action: handleForward,
            tooltip: t("emails.actions.forward"),
        },
    ].filter(Boolean) as MailActionItem[];

    return (
        <StopPropagateDiv
            className={cn(
                "mail-actions-container inline-flex w-fit items-center gap-1.5 rounded-md",
                variant === MailActionVariant.MAIL_LIST &&
                    "border border-neutral-lighter bg-white px-2 py-1 shadow-md",
                className,
            )}
        >
            <MailActionButtons
                actions={actions}
                onTooltipOpenChange={onTooltipOpenChange}
                variant={variant}
            />
        </StopPropagateDiv>
    );
};

interface MailMassActionProps {
    className?: string;
    selectedMails: Email[];
    setSelectedMails: (selectedMails: Email[]) => void;
    listId: MailsListId;
}

const MailMassAction = ({
    className,
    selectedMails,
    setSelectedMails,
    listId,
}: MailMassActionProps) => {
    const { t } = useTranslation();

    const [areAllRead, setAreAllRead] = useState(
        selectedMails.every((mail) => mail.isRead),
    );
    const [areAllProcessed, setAreAllProcessed] = useState(
        selectedMails.every((mail) => mail.isUserProcessed),
    );
    const [areAllFocused, setAreAllFocused] = useState(
        selectedMails.every((mail) => mail.isFocus),
    );

    const {
        setReadStatusBulk,
        setProcessStatusBulk,
        setMailFocusBulk,
        deleteMailsBulk,
    } = useMailActions();

    useEffect(() => {
        setAreAllRead(selectedMails.every((mail) => mail.isRead));
        setAreAllProcessed(selectedMails.every((mail) => mail.isUserProcessed));
        setAreAllFocused(selectedMails.every((mail) => mail.isFocus));
    }, [selectedMails]);

    const handleUpdateMailsList = async (action: () => Promise<unknown>) => {
        await action();
        setSelectedMails([]);
    };

    const mailsAreAnalyzed = selectedMails.every(
        (mail) => mail.aiDetails !== undefined,
    );

    const actions = [
        {
            icon: areAllRead ? MailClosedIcon : MailIcon,
            action: () => {
                handleUpdateMailsList(() =>
                    setReadStatusBulk.mutateAsync({
                        mails: selectedMails,
                        listId,
                        readStatus: !areAllRead,
                    }),
                );
            },
            tooltip: areAllRead
                ? t("emails.actions.markAsUnread")
                : t("emails.actions.markAsRead"),
        },
        ...(mailsAreAnalyzed
            ? [
                  {
                      icon: areAllProcessed ? CircleIcon : CheckCircleBroken,
                      action: () => {
                          handleUpdateMailsList(() =>
                              setProcessStatusBulk.mutateAsync({
                                  mails: selectedMails,
                                  listId,
                                  isProcessed: !areAllProcessed,
                              }),
                          );
                      },
                      tooltip: areAllProcessed
                          ? t("emails.actions.markAsUnprocessed")
                          : t("emails.actions.markAsProcessed"),
                  },
                  ...(listId !== SmartLabel.Traites
                      ? [
                            {
                                icon: areAllFocused
                                    ? TargetBarIcon
                                    : TargetIcon,
                                action: () => {
                                    handleUpdateMailsList(() =>
                                        setMailFocusBulk.mutateAsync({
                                            mails: selectedMails,
                                            listId,
                                            isFocus: !areAllFocused,
                                        }),
                                    );
                                },
                                tooltip: areAllFocused
                                    ? t("emails.actions.removeFromFocus")
                                    : t("emails.actions.moveToFocus"),
                            },
                        ]
                      : []),
              ]
            : []),
        {
            icon: TrashIcon,
            action: async () => {
                handleUpdateMailsList(() =>
                    deleteMailsBulk.mutateAsync({
                        mails: selectedMails,
                        listId,
                    }),
                );
            },
            tooltip: t("emails.actions.delete"),
        },
    ].filter(Boolean) as MailActionItem[];

    return (
        <StopPropagateDiv className={cn("flex gap-2", className)}>
            <MailActionButtons actions={actions} />
        </StopPropagateDiv>
    );
};

export { MailAction, MailMassAction };
